import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppConnectionState } from '../../../contexts/ConnectionStateContext';
import { useInitialLoadingScreenManager } from '../../../hooks/useInitialLoadingScreenManager';
import { ChevronLeft, Mic, Plus } from 'lucide-react';
import { Loader } from '../../../components/ui';
import { cn } from '../../../lib/utils';
import { useVoiceRecording } from '../../../hooks/useVoiceRecording';
import type { Topic } from '../../../types/topic';
import { EmptyList } from './EmptyList';
import { ListItem } from './ListItem';
import { VoiceRecorder } from '../../shared/VoiceRecorder';
import { TopicInput } from '../../shared/TopicInput';
import { logger } from '../../../utils/logger';
import { useViewport } from '../../../contexts/ViewportContext';
import { useStatusBarInfo } from '../../../hooks/useStatusBarInfo';
import {
  useListItems,
  useAddListItem,
  useUpdateListItem,
  useDeleteListItem,
} from '../../../hooks/queries/useLists';
import { useTopicSubscription } from '../../../hooks/useTopicSubscription';
import { listKeys } from '../../../hooks/queries/listKeys';

interface ListTopicProps {
  topic: Topic;
  hasCollaborators?: boolean;
  onBackClick: () => void;
}

export function ListTopic({ topic, onBackClick }: ListTopicProps) {
  const [input, setInput] = useState('');
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showFooterShadow, setShowFooterShadow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // React Query hooks
  const { data: listItemsData, isLoading: loading, error } = useListItems(topic.id);
  const addListItemMutation = useAddListItem();
  const updateListItemMutation = useUpdateListItem();
  const deleteListItemMutation = useDeleteListItem();

  // Memoize list items array to prevent infinite re-renders
  const listItems = useMemo(() => listItemsData || [], [listItemsData]);

  // Set up real-time subscription for list items
  // Memoize subscriptions to prevent unnecessary re-subscriptions
  const subscriptions = useMemo(
    () => [{ table: 'list_items' as const, queryKey: listKeys.byTopic(topic.id) }],
    [topic.id]
  );

  useTopicSubscription(topic.id, subscriptions);

  const { isMobile } = useViewport();
  const { isNativeApp, statusBarHeight, navigationBarHeight } = useStatusBarInfo();
  useAppConnectionState();
  const { markRouteComplete } = useInitialLoadingScreenManager();

  // Only mark as complete when loading is done
  // Using a ref to track if we've already marked complete to avoid infinite updates
  const hasMarkedCompleteRef = useRef(false);
  useEffect(() => {
    if (!loading && !hasMarkedCompleteRef.current) {
      hasMarkedCompleteRef.current = true;
      markRouteComplete();
    }
  }, [loading]); // Remove markRouteComplete from deps to prevent infinite updates

  const {
    isRecording,
    recordingTime,
    error: recordingError,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecording();

  // Update footer shadow when list items change
  useEffect(() => {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      if (contentRef.current) {
        const container = contentRef.current;
        const hasOverflow = container.scrollHeight > container.clientHeight;
        const scrollTop = container.scrollTop;
        const isAtBottom =
          Math.abs(container.scrollHeight - container.clientHeight - scrollTop) < 10;

        setShowFooterShadow(hasOverflow && !isAtBottom);
      }
    }, 100);
  }, [listItems.length]);

  // Add scroll listener to detect when to show header and footer shadows
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const container = contentRef.current;
        const scrollTop = container.scrollTop;

        // Show header shadow when scrolled down
        setIsScrolled(scrollTop > 10);

        // Show footer shadow only when:
        // 1. Content height exceeds the visible area AND
        // 2. User hasn't scrolled to the bottom
        const hasOverflow = container.scrollHeight > container.clientHeight;
        const isAtBottom =
          Math.abs(container.scrollHeight - container.clientHeight - scrollTop) < 10;

        setShowFooterShadow(hasOverflow && !isAtBottom);
      }
    };

    const scrollContainer = contentRef.current;
    if (scrollContainer) {
      // Run the check after a small delay to ensure content is rendered
      setTimeout(handleScroll, 100);

      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    try {
      logger.component('Adding list item:', { topicId: topic.id, input });
      await addListItemMutation.mutateAsync({
        topicId: topic.id,
        text: input,
      });
      setInput('');
    } catch (error) {
      console.error('Failed to add item:', error);
    }
  };

  const handleEdit = async (itemId: string, text: string) => {
    try {
      logger.component('Updating list item:', { topicId: topic.id, itemId, text });
      await updateListItemMutation.mutateAsync({
        topicId: topic.id,
        itemId,
        updates: { text },
      });
    } catch (error) {
      console.error('Failed to edit item:', error);
    }
  };

  const handleDelete = async (itemId: string) => {
    try {
      logger.component('Deleting list item:', { topicId: topic.id, itemId });

      await deleteListItemMutation.mutateAsync({
        topicId: topic.id,
        itemId,
      });
      setItemToDelete(null);
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
  };

  const handleVoiceClick = async () => {
    if (isRecording) {
      try {
        const result = await stopRecording();
        const text = result?.transcription || '';

        if (text.trim()) {
          logger.component('Adding voice list item:', { topicId: topic.id, text });
          await addListItemMutation.mutateAsync({
            topicId: topic.id,
            text,
          });
        }
      } catch (error) {
        console.error('Error processing voice message:', error);
      }
    } else {
      try {
        await startRecording();
      } catch (error) {
        console.error('Error starting recording:', error);
      }
    }
  };

  // Convert FormattedListItem to ListItemType for compatibility with existing ListItem component
  const convertedListItems = listItems.map(item => ({
    id: item.id,
    topic_id: item.topicId,
    user_id: item.userId,
    text: item.text,
    order_index: item.orderIndex.toString(),
    created_at: item.createdAt,
    updated_at: item.updatedAt,
  }));

  return (
    <div className="flex flex-col h-full">
      {/* Mobile header with shadow on scroll */}
      <div
        className={`lg:hidden flex items-center px-4 sticky top-0 z-10 transition-shadow duration-200 ${
          isScrolled ? 'shadow-md' : ''
        }`}
        style={{
          paddingTop: isMobile && isNativeApp ? `${statusBarHeight}px` : '0',
          height: isMobile && isNativeApp ? `calc(3.5rem + ${statusBarHeight}px)` : '3.5rem',
        }}
      >
        <div className="flex-none">
          <button onClick={onBackClick} className="p-2 -ml-2 text-gray-600 hover:text-gray-900">
            <ChevronLeft size={24} />
          </button>
        </div>

        {/* Title - centered relative to viewport */}
        <div className="absolute left-1/2 -translate-x-1/2">
          <span className="text-base font-semibold text-gray-900 whitespace-nowrap">
            {topic.name}
          </span>
        </div>

        {/* Spacer for centering */}
        <div className="flex-none w-10" />
      </div>

      {/* List */}
      <div ref={contentRef} className="flex-1 overflow-y-auto">
        {/* Display loading state */}
        {loading ? (
          <div className="h-full w-full flex items-center justify-center bg-gray-50">
            <Loader color="text-blue-500" />
          </div>
        ) : (
          <div
            className="p-6"
            style={{
              paddingBottom:
                isMobile && isNativeApp ? `calc(1.5rem + ${navigationBarHeight}px)` : '1.5rem',
            }}
          >
            {/* Display error state */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm mb-4">
                Error loading list items: {error instanceof Error ? error.message : String(error)}
              </div>
            )}

            {/* Display empty state when no items and not loading */}
            {!loading && !error && convertedListItems.length === 0 ? (
              <EmptyList />
            ) : (
              <div className="flex flex-col gap-2">
                {convertedListItems.map(item => (
                  <ListItem
                    key={item.id}
                    item={item}
                    itemToDelete={itemToDelete}
                    onDelete={handleDelete}
                    onDeleteClick={setItemToDelete}
                    onCancelDelete={() => setItemToDelete(null)}
                    onEdit={handleEdit}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input */}
      <div
        className={`w-full sticky bottom-0 z-10 transition-shadow duration-200 ${
          showFooterShadow
            ? 'lg:shadow-none md:shadow-none shadow-[0_-2px_4px_rgba(0,0,0,0.1)]'
            : ''
        }`}
        style={{
          paddingBottom: isMobile && isNativeApp ? `${navigationBarHeight}px` : '0',
        }}
      >
        {recordingError && (
          <div className="p-3">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm">
              {recordingError}
            </div>
          </div>
        )}

        <VoiceRecorder
          isRecording={isRecording}
          recordingTime={recordingTime}
          onCancel={cancelRecording}
        />

        <div className="py-4 px-6">
          <TopicInput
            input={input}
            setInput={setInput}
            placeholder="Add new item..."
            isRecording={isRecording}
            onSubmit={handleSubmit}
            isTextarea={false}
            buttons={[
              {
                id: 'voice-recording',
                icon: <Mic size={20} className={cn(isRecording && 'animate-pulse')} />,
                onClick: handleVoiceClick,
                title: isRecording ? 'Stop recording' : 'Start voice recording',
                isActive: isRecording,
                color: 'blue',
                showWhenEmpty: true,
                hideWhenTyping: input.trim().length > 0, // Hide when there's text
              },
              {
                id: 'add-item',
                icon: <Plus size={20} />,
                onClick: () => {}, // Will use form's submit handler
                title: 'Add item',
                disabled: !input.trim(),
                color: 'blue',
                isSubmitButton: true,
                hideWhenRecording: true,
                // Will be hidden when input is empty due to the logic in TopicInput
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
